import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function Terms() {
    return (
        <main className="bg-gradient-to-b from-slate-900 to-black text-gray-100 py-16 overflow-hidden">
            <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row items-start gap-12">

                {/* 🔹 Text Section */}
                <motion.div
                    className="flex-1 z-10"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                >
                    <h1 className="text-4xl sm:text-5xl font-bold text-white">
                        Terms of Service
                    </h1>
                    <p className="mt-2 text-sm text-gray-400">
                        Last updated: {new Date().toLocaleDateString()}
                    </p>

                    <p className="mt-6 text-lg text-gray-300 leading-relaxed">
                        These Terms of Service (“Terms”) govern access to and use of SecureID
                        Technologies LLC’s software and services (the “Services”). By using the
                        Services, you agree to these Terms. If you are entering into these Terms on
                        behalf of an organization, you represent that you have authority to bind that
                        organization.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">1) Services & Evaluation</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        SecureID provides identity and testing solutions including DactylScan Live Scan,
                        ExamsPro, and the Background Check Portal. Evaluation or demo versions may be
                        provided “as is” and may be time- or feature-limited.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">2) Customer Responsibilities</h2>
                    <ul className="mt-3 list-disc pl-6 text-gray-300 space-y-2">
                        <li>Maintain accurate account information and safeguard credentials.</li>
                        <li>Comply with applicable laws, including data protection and biometric laws.</li>
                        <li>Provide legally sufficient notices and consents where required.</li>
                        <li>Use the Services only for lawful and documented purposes.</li>
                    </ul>

                    <h2 className="mt-10 text-2xl font-semibold text-white">3) License & Restrictions</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        SecureID grants a limited, non-exclusive license to use the Services for
                        internal business purposes. You may not reverse engineer, resell, or use the
                        Services to develop a competing product.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">4) Customer Data & Privacy</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        You retain all rights to Customer Data. SecureID processes it only to provide
                        Services per contract. Both parties must implement reasonable security measures.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">5) Compliance Notes</h2>
                    <ul className="mt-3 list-disc pl-6 text-gray-300 space-y-2">
                        <li>
                            <strong>EBTS/FDLE:</strong> Software supports EBTS packaging and FDLE workflows;
                            customers remain responsible for compliance and credentials.
                        </li>
                        <li>
                            <strong>Biometrics:</strong> Customers are responsible for consent and retention
                            compliance (e.g., BIPA, CUBI).
                        </li>
                    </ul>

                    <h2 className="mt-10 text-2xl font-semibold text-white">6) Fees & Payment</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Fees are stated in your agreement and payable within 30 days. Late payments may
                        result in suspension of Services.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">7) Support & Availability</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Support levels and uptime targets are defined by your order or plan. Beta or
                        evaluation features may change without notice.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">8) Confidentiality</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Each party will protect the other’s confidential information with reasonable care,
                        use it only as permitted, and disclose it only to authorized personnel.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">9) Warranties & Disclaimers</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Except as stated in a written contract, Services are provided “as is,” without any
                        warranty, express or implied.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">10) Limitation of Liability</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        To the maximum extent permitted by law, neither party is liable for indirect or
                        consequential damages. Each party’s total liability is limited to the amount paid
                        in the 12 months preceding the claim.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">11) Indemnity</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Each party will defend and indemnify the other for third-party claims arising from
                        misuse, IP infringement, or unlawful activity, subject to agreed limitations.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">12) Termination</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        Either party may terminate for uncured material breach after 30 days’ notice. Upon
                        termination, you must stop using the Services and delete our software.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">13) Governing Law</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        These Terms are governed by Florida law, with exclusive venue in Florida courts.
                    </p>

                    <h2 className="mt-10 text-2xl font-semibold text-white">14) Contact</h2>
                    <p className="mt-3 text-gray-300 leading-relaxed">
                        SecureID Technologies LLC, Vero Beach, FL, USA<br />
                        Email:{" "}
                        <a
                            href="mailto:alberto.perez@secureidtechnologies.com"
                            className="text-indigo-400 hover:underline"
                        >
                            alberto.perez@secureidtechnologies.com
                        </a>
                    </p>

                    <p className="mt-10 text-xs text-gray-500">
                        These Terms are provided for informational purposes only and do not constitute legal advice.
                    </p>

                    <p className="mt-10 text-center lg:text-left text-sm text-gray-400">
                        <Link to="/" className="text-indigo-400 hover:underline">
                            ← Back to Home
                        </Link>
                    </p>
                </motion.div>

                {/* 🔹 Animated Image Section */}
                <motion.div
                    className="flex-1 flex justify-center"
                    initial={{ opacity: 0, x: 200 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                >
                    <img
                        src="/Images/terms-secureid.png"
                        alt="Legal and compliance illustration"
                        className="w-[90%] max-w-md rounded-2xl shadow-2xl"
                    />
                </motion.div>
            </section>
        </main>
    );
}
